import React from 'react';
import { Grid, Button, Snackbar, Fade, Box } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Title from './Title';
import axios from 'axios';
import jwt from 'jsonwebtoken';

import { useDispatch, useSelector } from 'react-redux';
import {
    setAddress,
    setDID,
    setPrivateKey,
    setPublicKey,
    _address,
    _did,
    _privateKey,
    _publicKey
} from '../redux/offCreatorSlice';
import TextWithCopy from './TextFieldWithCopy';
import DescriptionBox from './DescriptionBox';
import Wallet from 'did-hd-wallet';
import { utils as sdVcUtils } from 'sd-vc-lib';
import KeyMethod from 'zedeid-did-method-key/dist/index';
import { WizardButton } from './ui/WizardButton';
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined';
import { trackEvent } from '../utils/google-analytics';

const Ed25519 = sdVcUtils.ed25519;

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            '& > * + *': {
                marginTop: theme.spacing(2)
            }
        },
        btn: {
            marginBottom: 10
        }
    })
);

export default function OCDIDCreator() {
    const did = useSelector(_did);
    const privateKey = useSelector(_privateKey);
    const publicKey = useSelector(_publicKey);
    const address = useSelector(_address);
    const classes = useStyles();

    const [snackBarState, setState] = React.useState<{ open: boolean; text: string }>({
        open: false,
        text: ''
    });

    const dispatch = useDispatch();

    async function generateDID() {
        const ed = new Ed25519();
        const createUrl = `${process.env.REACT_APP_DID_RESOLVER_URL}/did/key`;
        const challengeUrl = `${process.env.REACT_APP_DID_RESOLVER_URL}/challenge`;

        const wallet = new Wallet();
        const mnemonic = wallet.generateMnemonic(128);
        wallet.fromSeed(wallet.mnemonicToSeed(mnemonic));

        const { did, privateKey, publicKey, verificationKey, address, didDocument } =
            await wallet.getMasterKeys();

        const holderVerificationKey = await new KeyMethod().createVerificationMethod(privateKey);

        const challengeResponse = await axios.post(
            challengeUrl,
            { did },
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );
        const challengeToken = challengeResponse.data.challengeToken;
        const { challenge } = jwt.decode(challengeToken) as { challenge: string };

        const cipherText = ed
            .sign(Buffer.from(challenge, 'hex'), Buffer.from(privateKey as string, 'hex'))
            .toHex();

        const requestBody = {
            did,
            cipherText,
            publicKey,
            verificationKey: holderVerificationKey,
            didDocument: didDocument
        };

        const createResponse = await axios.put(createUrl, requestBody, {
            headers: {
                'Content-Type': 'application/json',
                Authentication: challengeToken
            }
        });

        if (!createResponse?.data?.did && createResponse?.data?.status !== 'success') {
            console.log(createResponse);
            const error = 'Error. Please try again later';
            dispatch(setDID(error));
            dispatch(setPrivateKey(error));
            dispatch(setPublicKey(error));
            dispatch(setAddress(error));
            return;
        }

        dispatch(setDID(did));
        dispatch(setPrivateKey(privateKey as string));
        dispatch(setPublicKey(publicKey as string));
        dispatch(setAddress(address));
        trackEvent('DID Tools', 'did_created', 'offchain_did');
    }

    function handleClose() {
        setState({
            ...snackBarState,
            open: false
        });
    }

    function callback(success: boolean) {
        if (success) {
            setState({
                open: true,
                text: 'Copied to clipboard'
            });
        } else {
            setState({
                open: true,
                text: 'Clould not copied'
            });
        }
    }

    return (
        <div>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        style={{ gap: 16 }}>
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 10,
                                width: 38,
                                height: 38,
                                backgroundColor: '#2ec4b6',
                                color: '#fff'
                            }}>
                            <AddBoxOutlinedIcon style={{ color: '#ffffff' }} />
                        </Box>
                        <Title>Generate Offchain DID</Title>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <div
                        style={{
                            border: '1px solid #2ec4b6',
                            borderRadius: 12,
                            padding: 16,
                            backgroundColor: '#fff'
                        }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <DescriptionBox
                                    description="You could create an Key address along with a private 
                                key and register that as a Decentralised Identity here. 
                                Click 'Generate DID' to generate a DID."
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <WizardButton
                                    data-testid="generateDID"
                                    onClick={generateDID}
                                    variant="contained"
                                    style={{ textTransform: 'uppercase' }}
                                    endIcon={<AddBoxOutlinedIcon />}>
                                    Generate DID
                                </WizardButton>
                            </Grid>

                            <Grid item xs={12}>
                                <TextWithCopy label="Address" value={address} callback={callback} />
                            </Grid>

                            <Grid item xs={12}>
                                <TextWithCopy
                                    label="Private key"
                                    value={privateKey}
                                    callback={callback}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextWithCopy
                                    label="Public key"
                                    value={publicKey}
                                    callback={callback}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextWithCopy
                                    label="Decentralized ID (DID)"
                                    value={did}
                                    callback={callback}
                                />
                            </Grid>
                        </Grid>
                    </div>
                </Grid>
            </Grid>

            <Snackbar
                open={snackBarState.open}
                onClose={handleClose}
                TransitionComponent={Fade}
                message={snackBarState.text}
                autoHideDuration={5000}
            />
        </div>
    );
}
